import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { useDecodedToken } from './authorization.hook';
import { View } from '@im/ui-lib/View';
import { useSiteConfig } from '../config';
import { useLocalizationContext } from '@im/react-localization';
import { useFeatureFlag } from '../feature-flag';

type Props = {
  children: JSX.Element;
  env?: string;
  siteCode?: string;
};

const acrValue = 'urn:okta:loa:2fa:any';

const boKeyEnabled = 'Step-Up_Auth_Enabled';
const boKeyWhitelist = 'Step-Up_Auth_Reseller_Whitelist';
const boKeyBlacklist = 'Step-Up_Auth_Reseller_Blacklist';

export function RequireStepUpAuth(props: Readonly<Props>) {
  const { children } = props;
  const siteCodes = props.siteCode?.split(',');
  const environments = props.env?.split(',');
  const { environment, siteCode } = useSiteConfig();
  const oktaContext = useOktaAuth();
  const authState = oktaContext?.authState;
  const oktaAuth = oktaContext?.oktaAuth;
  const location = useLocation();
  const token = useDecodedToken();
  const [isStepUpRequired, setStepUpRequired] = useState<boolean | null>(null);
  const { locale } = useLocalizationContext();

  const isRequired =
    (!siteCodes || siteCodes?.includes(siteCode)) &&
    (!environments || environments?.includes(environment));

  const boKeys = useFeatureFlag([boKeyEnabled, boKeyWhitelist, boKeyBlacklist]);
  const [boKeyIsReady, setBoKeyIsReady] = useState<boolean>(false);
  const boKeyValueRef = useRef(boKeys.value);

  useEffect(() => {
    if (boKeys.isReady) {
      boKeyValueRef.current = boKeys.value;
      setBoKeyIsReady(true);
    }
  }, [boKeys]);

  useEffect(() => {
    if (authState?.isAuthenticated && boKeyIsReady) {
      const boKeyValues = boKeyValueRef.current;
      const enabled = boKeyValues[boKeyEnabled]?.toLowerCase() === 'true';
      const resellerWhitelist = convertToArray(boKeyValues[boKeyWhitelist]);
      const resellerBlacklist = convertToArray(boKeyValues[boKeyBlacklist]);
      const resellerId = token?.resellerId.toLowerCase().trim();
      const isEnabled =
        enabled &&
        resellerId &&
        (resellerWhitelist.includes('*') ||
          resellerWhitelist.includes(resellerId)) &&
        !resellerBlacklist.includes(resellerId);

      if (token?.acr === acrValue || !isRequired || !isEnabled) {
        setStepUpRequired(false);
      } else {
        oktaAuth.token
          .getWithPopup({
            extraParams: {
              acr_values: acrValue,
              locale: locale,
            },
          })
          .then((tokenResponse) => {
            const tokens = tokenResponse.tokens;
            oktaAuth.tokenManager.setTokens(tokens);
          })
          .catch((err) => {
            console.error('authorize call retuned an error: ', err);
            setStepUpRequired(true);
          });
      }
    }
  }, [
    authState,
    boKeyIsReady,
    isRequired,
    locale,
    oktaAuth,
    setStepUpRequired,
    token,
  ]);

  if (!authState?.isAuthenticated) {
    return <Navigate state={{ from: location }} to="/login" />;
  }

  if (isStepUpRequired) {
    return (
      <View>
        <div>
          <p>Step-up authentication is required!</p>
        </div>
      </View>
    );
  } else if (isStepUpRequired === false) {
    return children;
  }

  return (
    <View>
      <div>
        <p>Pending step-up authentication...</p>
      </div>
    </View>
  );
}

const convertToArray = (str: string) => {
  if (!str) {
    return [];
  }
  return str
    .split(/[,;]+/)
    .map((x) => x.toLowerCase().trim())
    .filter((x) => x);
};
